import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import useSEO from "@/lib/useSEO";
import { SERVICES, SITE } from "@/lib/site";
import EkgLine from "@/components/EkgLine";

export default function Services() {
    useSEO({
        title: `Services — ${SITE.brand}`,
        description: `Interior, exterior, commercial, and specialty painting services in ${SITE.serviceArea}. See our process and request an estimate.`,
    });

    return (
        <main data-testid="page-services">
            {/* Page hero */}
            <section
                style={{ background: "var(--brand-cream)" }}
                className="section-pad"
            >
                <div className="brand-container fade-rise">
                    <span className="eyebrow">Our Services</span>
                    <h1
                        className="h1"
                        style={{ marginTop: "1rem", marginBottom: "1rem" }}
                        data-testid="services-headline"
                    >
                        Quality finishes that hold up to{" "}
                        <span style={{ color: "var(--brand-red)" }}>
                            real life
                        </span>
                        .
                    </h1>
                    <p
                        style={{
                            color: "var(--brand-muted)",
                            fontSize: "1.125rem",
                            maxWidth: 680,
                        }}
                    >
                        Every job â big or small â gets the same prep, the same
                        crews, and the same standard. Here's the work we do for
                        builders, businesses, and homeowners across Utah and the
                        surrounding states.
                    </p>
                </div>
            </section>

            {/* Service blocks */}
            {SERVICES.map((s, idx) => (
                <section
                    key={s.slug}
                    id={s.slug}
                    style={{
                        background: idx % 2 === 0 ? "#fff" : "var(--brand-cream)",
                        borderTop: "1px solid var(--brand-surface)",
                    }}
                    className="section-pad"
                    data-testid={`service-section-${s.slug}`}
                >
                    <div
                        className="brand-container"
                        style={{
                            display: "grid",
                            gap: "3rem",
                            gridTemplateColumns:
                                idx % 2 === 0
                                    ? "minmax(0, 1fr) minmax(0, 1fr)"
                                    : "minmax(0, 1fr) minmax(0, 1fr)",
                            alignItems: "center",
                        }}
                    >
                        <div
                            style={{
                                order: idx % 2 === 0 ? 0 : 1,
                            }}
                        >
                            <div
                                style={{
                                    aspectRatio: "4 / 3",
                                    borderRadius: 14,
                                    overflow: "hidden",
                                    boxShadow:
                                        "0 12px 36px rgba(26,26,26,0.10)",
                                    background: "var(--brand-surface)",
                                }}
                                data-testid={`service-image-${s.slug}`}
                            >
                                <img
                                    src={s.image}
                                    alt={`${s.title} example`}
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        display: "block",
                                    }}
                                    onError={(e) => {
                                        e.currentTarget.style.display = "none";
                                    }}
                                />
                            </div>
                        </div>

                        <div>
                            <span className="eyebrow">
                                0{idx + 1} · {s.title}
                            </span>
                            <h2
                                className="h2"
                                style={{
                                    marginTop: "0.85rem",
                                    marginBottom: "1rem",
                                }}
                            >
                                {s.title}
                            </h2>
                            <p
                                style={{
                                    color: "var(--brand-muted)",
                                    fontSize: "1.0625rem",
                                    marginBottom: "1.5rem",
                                }}
                            >
                                {s.long}
                            </p>

                            <h4
                                style={{
                                    fontFamily: "var(--font-display)",
                                    fontSize: "0.875rem",
                                    fontWeight: 800,
                                    letterSpacing: "0.08em",
                                    textTransform: "uppercase",
                                    color: "var(--brand-dark)",
                                    marginBottom: "0.75rem",
                                }}
                            >
                                Our Process
                            </h4>
                            <ol
                                style={{
                                    listStyle: "none",
                                    padding: 0,
                                    margin: "0 0 1.75rem",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "0.65rem",
                                }}
                            >
                                {s.steps.map((step, i) => (
                                    <li
                                        key={step}
                                        style={{
                                            display: "flex",
                                            gap: "0.85rem",
                                            alignItems: "flex-start",
                                            color: "var(--brand-dark)",
                                            fontSize: "0.9375rem",
                                        }}
                                        data-testid={`service-step-${s.slug}-${i + 1}`}
                                    >
                                        <span
                                            style={{
                                                flex: "none",
                                                width: 26,
                                                height: 26,
                                                borderRadius: 7,
                                                background: "var(--brand-red)",
                                                color: "#fff",
                                                fontFamily:
                                                    "var(--font-display)",
                                                fontWeight: 800,
                                                fontSize: "0.8125rem",
                                                display: "grid",
                                                placeItems: "center",
                                            }}
                                            aria-hidden="true"
                                        >
                                            {i + 1}
                                        </span>
                                        {step}
                                    </li>
                                ))}
                            </ol>

                            <Link
                                to="/contact"
                                className="btn btn-primary"
                                data-testid={`service-cta-${s.slug}`}
                            >
                                Estimate this work
                                <ArrowRight size={18} />
                            </Link>
                        </div>
                    </div>
                </section>
            ))}

            {/* Bottom CTA */}
            <section
                style={{ background: "var(--brand-dark)", color: "#fff" }}
                data-testid="services-bottom-cta"
            >
                <EkgLine height={36} />
                <div
                    className="brand-container section-pad"
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                        gap: "1.25rem",
                    }}
                >
                    <h2
                        className="h2"
                        style={{
                            color: "#fff",
                            maxWidth: 720,
                            fontSize: "clamp(1.75rem, 3.5vw, 2.5rem)",
                        }}
                    >
                        Ready to start your project?
                    </h2>
                    <p style={{ color: "#cfcdc6", maxWidth: 540 }}>
                        Tell us about the space and we'll come take a look.
                        Estimates are written, detailed, and free.
                    </p>
                    <div
                        style={{
                            display: "flex",
                            gap: "0.75rem",
                            flexWrap: "wrap",
                            justifyContent: "center",
                        }}
                    >
                        <Link
                            to="/contact"
                            className="btn btn-on-dark"
                            data-testid="services-cta-quote"
                        >
                            Get a Free Estimate
                            <ArrowRight size={18} />
                        </Link>
                        <a
                            href={`tel:${SITE.phoneLink}`}
                            className="btn"
                            data-testid="services-cta-call"
                            style={{
                                background: "transparent",
                                color: "#fff",
                                borderColor: "#fff",
                            }}
                        >
                            Or call {SITE.phoneDisplay}
                        </a>
                    </div>
                </div>
            </section>
        </main>
    );
}
